// Parses a JSON-looking string into its structural value; returns the
// input unchanged if it's not a string or doesn't parse as JSON.
function parseJsonish(v) {
  if (typeof v === 'string') {
    const t = v.trim()
    if (t[0] === '[' || t[0] === '{') {
      try { return JSON.parse(t) } catch (e) { /* not json, leave as-is */ }
    }
  }
  return v
}

// Recursively compare two values, treating null / undefined / '' as equivalent.
// This handles fields the server omits entirely being set to '' by v-model inputs.
// JSON strings are parsed and compared structurally so that:
//   - whitespace differences in serialisation don't register as dirty
//   - '[]' / '{}' are treated as equivalent to '' (empty field)
//   - a JSON string compares equal to its already-parsed object/array
//     counterpart (save methods parse these fields in place before diffing)
export function deepEqual(a, b) {
  const emptyish = v => v === null || v === undefined || v === ''
  const emptyJson = v => v === '[]' || v === '{}'
  if (emptyish(a) && emptyish(b)) return true
  if ((emptyish(a) || emptyJson(a)) && (emptyish(b) || emptyJson(b))) return true
  if (a === b) return true
  // If either side is a JSON string, parse it and compare structurally against the other side.
  if (typeof a === 'string' || typeof b === 'string') {
    const pa = parseJsonish(a)
    const pb = parseJsonish(b)
    if (pa !== a || pb !== b) return deepEqual(pa, pb)
  }
  if (Array.isArray(a) && Array.isArray(b)) {
    if (a.length !== b.length) return false
    return a.every((v, i) => deepEqual(v, b[i]))
  }
  if (a && b && typeof a === 'object' && typeof b === 'object') {
    const keys = new Set([...Object.keys(a), ...Object.keys(b)])
    for (const key of keys) {
      if (!deepEqual(a[key], b[key])) return false
    }
    return true
  }
  return false
}

export default {
  data() {
    return {
      modelSnapshot: null,
      saving: false,
    }
  },
  computed: {
    isDirty() {
      if (this.modelSnapshot === null) return false
      const current = JSON.stringify(this.model)
      // Fast path: identical serialisation
      if (current === this.modelSnapshot) return false
      // Slow path: normalised comparison for null/undefined/'' equivalence
      return !deepEqual(this.model, JSON.parse(this.modelSnapshot))
    }
  },
  methods: {
    // Pulls the server's parsed error message out of a failed save/load
    // request (execute() in networking/index.js already extracts this same
    // shape internally but discards it before re-throwing), falling back to
    // a generic message so callers don't need to duplicate this shape-sniffing.
    _saveErrorMessage(e) {
      const data = e && e.response && e.response.data
      return (data && data.message) ||
        (data && data.data && data.data.error) ||
        'error updating data, try logging in again or contact support if needed'
    },
    _resetDirtyTracking() {
      this.modelSnapshot = JSON.stringify(this.model)
      // Clear any stale validation errors and pending timers from the
      // numeric/filter/json/required-when-enabled mixins so a discard (or the
      // silent reload after a successful save) doesn't leave a stale error
      // around that silently blocks every future save attempt. jsonErrors in
      // particular is otherwise only cleared by the `loading` watcher, which
      // never fires on a silent load — so a single bad raw-JSON edit could
      // wedge saving indefinitely, with the "Invalid JSON" toast being the
      // only (easy to miss) clue why.
      if ('numericErrors' in this) this.numericErrors = {}
      if ('filterErrors' in this) this.filterErrors = {}
      if ('jsonErrors' in this) this.jsonErrors = {}
      if ('requiredErrors' in this) this.requiredErrors = {}
      if (this._numericTimers) { Object.values(this._numericTimers).forEach(clearTimeout); this._numericTimers = {} }
      if (this._filterTimers) { Object.values(this._filterTimers).forEach(clearTimeout); this._filterTimers = {} }
      if (this._numericPendingArgs) this._numericPendingArgs = {}
      if (this._filterPendingArgs) this._filterPendingArgs = {}
    },
    // Returns only the model keys that differ from the snapshot, for partial saves.
    // Contract: any helper that posts a subset of this (e.g. a single-field/column
    // save) MUST also patch modelSnapshot for the keys it sent, or those keys will
    // read dirty forever and get re-sent on the next full save_config().
    _dirtyFields() {
      // Deliberate fallback: if we never loaded (snapshot is null), isDirty is
      // false so the Save button doesn't render and this path shouldn't be
      // reachable from the UI. It's a full-model send rather than a throw so a
      // stray call doesn't hard-crash the tab; a caller that hits this without
      // the button (e.g. via Enter-to-submit) is a bug to fix at the call site.
      if (this.modelSnapshot === null) return { ...this.model }
      const snapshot = JSON.parse(this.modelSnapshot)
      const changed = {}
      const allKeys = new Set([...Object.keys(this.model), ...Object.keys(snapshot)])
      for (const key of allKeys) {
        if (!deepEqual(this.model[key], snapshot[key])) {
          changed[key] = this.model[key]
        }
      }
      return changed
    },
    _firstDirtyField() {
      if (this.modelSnapshot === null) return null
      const snapshot = JSON.parse(this.modelSnapshot)
      const allKeys = new Set([...Object.keys(this.model), ...Object.keys(snapshot)])
      for (const key of allKeys) {
        if (!deepEqual(this.model[key], snapshot[key])) return key
      }
      return null
    },
    scrollToFirstDirtyField() {
      const field = this._firstDirtyField()
      if (!field) return

      // Resolve which accordion section (toggle key) this field belongs to
      let toggleKey = null

      // 1. JSON editor fields explicitly mapped in each component
      if (this.jsonFieldToggleMap && this.jsonFieldToggleMap[field]) {
        toggleKey = this.jsonFieldToggleMap[field]
      }

      // 2. Regular fields mapped via prefix convention in each component
      if (!toggleKey && this.fieldPrefixSectionMap) {
        for (const [prefix, key] of this.fieldPrefixSectionMap) {
          if (field.startsWith(prefix)) {
            toggleKey = key
            break
          }
        }
      }

      // Track whether section was already open BEFORE we open it
      const sectionWasClosed = toggleKey && this.toggle && !this.toggle[toggleKey]

      // Open the section if we found one
      if (toggleKey && this.toggle !== undefined) {
        this.$set(this.toggle, toggleKey, true)
      }

      // If we just opened a collapsed section wait for its CSS transition (~350ms)
      // before querying layout — otherwise the row is still at height 0
      const delay = sectionWasClosed ? 420 : 0

      const doHighlight = () => {
        // Find the exact field row, fall back to JSON editor element, then section card, then component root
        const row = field ? this.$el.querySelector(`[data-field="${field}"]`) : null
        const jsonEl = field ? this.$el.querySelector(`[data-json-field="${field}"]`) : null

        let scrollTarget = this.$el
        if (row) {
          scrollTarget = row
        } else if (jsonEl) {
          scrollTarget = jsonEl
        } else if (toggleKey) {
          const cards = this.$el.querySelectorAll('.alert-section-card')
          for (const card of cards) {
            if (card.getAttribute('data-section') === toggleKey) {
              scrollTarget = card
              break
            }
          }
        }

        scrollTarget.scrollIntoView({ behavior: 'smooth', block: 'center' })

        // For JSON editor fields flash the first data row (tr containing inputs)
        // inside the editor, falling back to the whole editor div. For regular
        // fields flash the first input, falling back to the row cells.
        let flashEl
        if (scrollTarget.tagName === 'TR') {
          const jsonEditor = scrollTarget.querySelector('[data-json-field]')
          if (jsonEditor) {
            flashEl = jsonEditor
          } else {
            const input = scrollTarget.querySelector('input, select, textarea, .custom-select')
            flashEl = input || scrollTarget
          }
        } else {
          flashEl = scrollTarget
        }

        flashEl.classList.remove('tc-error-highlight')
        void flashEl.offsetWidth
        flashEl.classList.add('tc-error-highlight')
        flashEl.addEventListener('animationend', () => flashEl.classList.remove('tc-error-highlight'), { once: true })
      }

      this.$nextTick(() => setTimeout(doHighlight, delay))
    },
  }
}
